import { LongPressable } from "@-/dev.improve.haniverse.components.ui.long-pressable";
import { SuspensePageBoundary } from "@-/dev.improve.haniverse.components.ui.suspense-page";
import { useMorphemeRouteBodyReady } from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.panels.route-body";
import { resolveRouteParam } from "@-/com.npmjs.expo-router";
import runes from "@-/com.npmjs.runes2";
import { usePrefetchedSuspenseQueries } from "@-/com.npmjs.tanstack__react-query";
import type { MorphemeHrefObject } from "@-/dev.improve.haniverse.lib.morpheme.href.entities";
import {
  useMorphemeMnemonicsPanelMorphemeSliceQueryOptions,
  useMorphemeMnemonicsPanelMorphemeUserSliceQueryOptions,
} from "@-/dev.improve.haniverse.lib.morpheme.read";
import { PrimaryControlsStripTitle } from "@-/dev.improve.haniverse.routes.nav._components.primary-controls-strip.title";
import { GraphemeFontGlyph } from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.grapheme.glyph.font";
import { MorphemeNotFound } from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.not-found";
import {
  GraphemeMorphemePanelsGateProvider,
  MorphemePanelsContextProvider,
} from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.panels._context";
import { GraphemePreviewSheetProvider } from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.panels._components.preview-sheet";
import { useGetGraphemeGlyph } from "@-/dev.improve.haniverse.routes.nav.app._lib.morpheme.grapheme.glyph/client";
import { MorphemePanelsWordView } from "#morpheme-panels-word-view";
import { MorphemePanelsLayoutSkeleton } from "#morpheme-panels-layout-skeleton";
import { copyToClipboard } from "@-/dev.improve.haniverse.lib.clipboard";
import { useCallback, useMemo, useRef, type ReactNode } from "react";
import type { MorphemeMnemonicsViewNavigationRefValue } from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.panels";
import { hasMinLength } from "@-/com.npmjs.ts-array-length";
import { morphemeSelectionItem } from "@-/dev.improve.haniverse.routes.nav.app._context";
import { useAppSelection } from "@-/dev.improve.haniverse.routes.nav.app._lib.morpheme.tile.selection";

function MorphemePanelsLayoutSuspense({
  children,
  morpheme,
  morphemeHrefObject,
}: {
  children: ReactNode;
  morpheme: string | string[];
  morphemeHrefObject: MorphemeHrefObject;
}) {
  const character = resolveRouteParam(morpheme);
  const mnemonicsPanelMorphemeSliceQueryOptions =
    useMorphemeMnemonicsPanelMorphemeSliceQueryOptions(character);
  const mnemonicsPanelMorphemeUserSliceQueryOptions =
    useMorphemeMnemonicsPanelMorphemeUserSliceQueryOptions(character);
  // One hook for both slices: `usePrefetchedSuspenseQueries` keeps the last good value per query-key
  // prefix as `placeholderData`, so a favorite/study write that invalidates the user slice (via
  // the actor change-version) refetches in place instead of re-suspending the layout and tearing
  // down the card panel below it.
  const [{ data: mnemonicsPanelMorphemeSlice }, { data: mnemonicsPanelMorphemeUserSlice }] =
    usePrefetchedSuspenseQueries({
      queries: [
        mnemonicsPanelMorphemeSliceQueryOptions,
        mnemonicsPanelMorphemeUserSliceQueryOptions,
      ],
    });

  // Inert nav plumbing: the mnemonics subtree still writes `viewNavigationRef` / calls
  // `setActiveIndex`, but for these routes nothing reads them (only the lesson footer does).
  const viewNavigationRef = useRef<MorphemeMnemonicsViewNavigationRefValue | null>(null);
  const noopSetActiveIndex = useCallback(() => {}, []);

  const getGraphemeGlyph = useGetGraphemeGlyph();
  const graphemeGlyph = getGraphemeGlyph(character);
  const selectionItems = useMemo(() => [morphemeSelectionItem(character)], [character]);
  const { consumePress, isSelected, onLongPress } = useAppSelection(selectionItems);

  // Distinguish word vs grapheme by codepoint count, NOT by whether the mnemonics slice is null: an
  // existing multi-char word (e.g. 我们) DOES resolve a non-null mnemonics slice, so keying off the
  // slice routes it into the grapheme two-panel view whose info tab then 404s. A word (>= 2
  // codepoints) always renders the word info view (its own header + info tabs, no toggle),
  // regardless of the slice; only a single grapheme uses the mnemonics slice + two-panel toggle.
  if (hasMinLength(runes(character), 2)) {
    return (
      <MorphemePanelsWordView
        character={character}
        morphemeHrefObject={morphemeHrefObject}
        noopSetActiveIndex={noopSetActiveIndex}
        viewNavigationRef={viewNavigationRef}
      />
    );
  }

  if (mnemonicsPanelMorphemeSlice === null) {
    return <MorphemeNotFound morpheme={character} />;
  }

  return (
    <MorphemePanelsContextProvider
      activeIndex={0}
      character={character}
      morphemeHrefObject={morphemeHrefObject}
      setActiveIndex={noopSetActiveIndex}
      viewNavigationRef={viewNavigationRef}
    >
      <GraphemeMorphemePanelsGateProvider
        readonlySlice={mnemonicsPanelMorphemeSlice}
        readonlySliceQueryKey={mnemonicsPanelMorphemeSliceQueryOptions.queryKey}
        userSlice={mnemonicsPanelMorphemeUserSlice}
        userSliceQueryKey={mnemonicsPanelMorphemeUserSliceQueryOptions.queryKey}
      >
        {/* Mnemonic links and decomposition components inside this subtree preview the linked
            grapheme in a sheet instead of navigating, so a reader can check a component without
            losing the character they were studying. */}
        <GraphemePreviewSheetProvider morphemeHrefObject={morphemeHrefObject}>
          <PrimaryControlsStripTitle>
            <LongPressable
              accessibilityLabel={`${character}, morpheme header`}
              accessibilityState={{ selected: isSelected }}
              className={isSelected ? "rounded-xs bg-primary/10" : undefined}
              hitSlop={12}
              onLongPress={onLongPress}
              onPress={() => {
                if (consumePress()) {
                  return;
                }
                void copyToClipboard(character, { label: character });
              }}
            >
              <GraphemeFontGlyph
                className="text-center text-[36px] leading-12.5 text-foreground"
                glyph={graphemeGlyph}
              />
            </LongPressable>
          </PrimaryControlsStripTitle>
          {children}
        </GraphemePreviewSheetProvider>
      </GraphemeMorphemePanelsGateProvider>
    </MorphemePanelsContextProvider>
  );
}

export function MorphemePanelsLayout({
  children,
  morpheme,
  morphemeHrefObject,
}: {
  children: ReactNode;
  morpheme: string | string[];
  morphemeHrefObject: MorphemeHrefObject;
}) {
  const onBodyReady = useMorphemeRouteBodyReady();
  // The card panel is the route's `view` search param.
  const routeView = resolveRouteParam(morphemeHrefObject.params.view);
  const tab = resolveRouteParam(morphemeHrefObject.params.tab);
  const view =
    routeView === "mnemonics" || (routeView !== "info" && tab === "mnemonics")
      ? "mnemonics"
      : "info";
  return (
    <SuspensePageBoundary
      fallback={<MorphemePanelsLayoutSkeleton morpheme={resolveRouteParam(morpheme)} view={view} />}
      location={import.meta.location}
      onErrorLayout={onBodyReady ?? undefined}
    >
      <MorphemePanelsLayoutSuspense morpheme={morpheme} morphemeHrefObject={morphemeHrefObject}>
        {children}
      </MorphemePanelsLayoutSuspense>
    </SuspensePageBoundary>
  );
}
